import {
  AloftArea,
  LocationType,
  SiteRating,
  SixTierQuality,
  isDefined,
} from '@aloft/toolkit-shared';

// Materiality thresholds for comp adjustment alerts. A material difference means subject and comp
// differ enough that an adjustment is typically expected; immaterial gaps are ignored.

// GLA: >15% difference vs subject sqft. Test: "treats GLA differences above/at or below 15%".
export const GLA_MATERIALITY_THRESHOLD = 0.15;
// Urban/Suburban site: >25% difference vs subject sqft. Test: "treats urban site size differences above/at or below 25%".
export const URBAN_SUBURBAN_SITE_SIZE_MATERIALITY_THRESHOLD = 0.25;
// Rural site: >1 acre absolute difference. Test: "treats rural site size differences above 1 acre".
export const RURAL_SITE_SIZE_MATERIALITY_ACRES = 1;
// Above-grade baths: >1 full bath or >2 half baths between subject and comp.
export const FULL_BATH_MATERIALITY_THRESHOLD = 1;
export const HALF_BATH_MATERIALITY_THRESHOLD = 2;
// Garage/carport: >1 parking space when both properties have the same parking type.
export const GARAGE_CARPORT_SPACE_MATERIALITY_THRESHOLD = 1;

// Returns true when |comp - subject| / subject sqft exceeds threshold. False when subject sqft is zero.
const exceedsSubjectPercentageThreshold = (
  subject: AloftArea,
  comp: AloftArea,
  threshold: number,
): boolean => {
  const subjectSqft = subject.toSqFt().unitAmount;
  if (subjectSqft === 0) return false;

  const compSqft = comp.toSqFt().unitAmount;
  return Math.abs(compSqft - subjectSqft) / subjectSqft > threshold;
};

// Gross living area. Material when comp GLA differs from subject by more than 15%.
// Zero or missing GLA is immaterial. Test: "treats GLA differences...", "treats zero GLA values...".
export const isGlaAdjustmentMaterial = (
  subjectGla: AloftArea | undefined,
  compGla: AloftArea | undefined,
): boolean => {
  if (!isDefined(subjectGla) || !isDefined(compGla)) return false;
  if (subjectGla.equals(AloftArea.zero) || compGla.equals(AloftArea.zero)) return false;

  return exceedsSubjectPercentageThreshold(subjectGla, compGla, GLA_MATERIALITY_THRESHOLD);
};

// Lot/site area. Urban and Suburban: material when sqft differs by more than 25% vs subject.
// Rural: material when absolute acre difference exceeds 1 acre (percentage rule does not apply).
// Zero or missing site area is immaterial. Test: "treats urban site size...", "treats rural site size...".
export const isSiteSizeAdjustmentMaterial = (
  subjectSite: AloftArea | undefined,
  compSite: AloftArea | undefined,
  locationType?: LocationType,
): boolean => {
  if (!isDefined(subjectSite) || !isDefined(compSite)) return false;
  if (subjectSite.equals(AloftArea.zero) || compSite.equals(AloftArea.zero)) return false;

  if (locationType === 'Rural') {
    const diffAcres = Math.abs(compSite.toAcre().unitAmount - subjectSite.toAcre().unitAmount);
    return diffAcres > RURAL_SITE_SIZE_MATERIALITY_ACRES;
  }

  return exceedsSubjectPercentageThreshold(
    subjectSite,
    compSite,
    URBAN_SUBURBAN_SITE_SIZE_MATERIALITY_THRESHOLD,
  );
};

// Site influence (e.g. Neutral vs Beneficial). Material when both ratings are present and differ.
// Test: "treats rating-level features as material only when ratings differ" (site influence case).
export const isSiteInfluenceAdjustmentMaterial = (
  subjectInfluence: SiteRating | undefined,
  compInfluence: SiteRating | undefined,
): boolean =>
  isDefined(subjectInfluence) && isDefined(compInfluence) && subjectInfluence !== compInfluence;

// View rating. Same rule as site influence: material only when both ratings exist and differ.
// Test: "treats rating-level features..." (view case with matching Neutral ratings).
export const isViewAdjustmentMaterial = (
  subjectView: SiteRating | undefined,
  compView: SiteRating | undefined,
): boolean => isDefined(subjectView) && isDefined(compView) && subjectView !== compView;

// UAD six-tier quality. Material when both tiers are present and differ (any tier step counts).
export const isQualityAdjustmentMaterial = (
  subjectQuality: SixTierQuality | undefined,
  compQuality: SixTierQuality | undefined,
): boolean => isDefined(subjectQuality) && isDefined(compQuality) && subjectQuality !== compQuality;

// UAD six-tier condition. Material when both tiers are present and differ.
// Test: "treats rating-level features..." (condition 4 vs 5 / 4 vs 4 cases).
export const isConditionAdjustmentMaterial = (
  subjectCondition: SixTierQuality | undefined,
  compCondition: SixTierQuality | undefined,
): boolean =>
  isDefined(subjectCondition) && isDefined(compCondition) && subjectCondition !== compCondition;

// Above-grade bath count. Material when full baths differ by more than 1, or half baths by more than 2.
// Full-bath check runs first; half-bath check requires both half-bath counts. Thresholds in test: "exports the documented thresholds".
export const isBathAdjustmentMaterial = (
  subjectFullBaths: number | undefined,
  subjectHalfBaths: number | undefined,
  compFullBaths: number | undefined,
  compHalfBaths: number | undefined,
): boolean => {
  if (!isDefined(subjectFullBaths) || !isDefined(compFullBaths)) return false;
  if (Math.abs(compFullBaths - subjectFullBaths) > FULL_BATH_MATERIALITY_THRESHOLD) return true;

  if (!isDefined(subjectHalfBaths) || !isDefined(compHalfBaths)) return false;
  return Math.abs(compHalfBaths - subjectHalfBaths) > HALF_BATH_MATERIALITY_THRESHOLD;
};

// Garage or carport. Material when presence differs, or when both have the feature, same parking type,
// and space count differs by more than 1. Missing has-feature flags make the check immaterial.
export const isGarageCarportAdjustmentMaterial = ({
  subjectHasFeature,
  compHasFeature,
  subjectSpaces,
  compSpaces,
  subjectParkingType,
  compParkingType,
}: {
  subjectHasFeature?: boolean;
  compHasFeature?: boolean;
  subjectSpaces?: number;
  compSpaces?: number;
  subjectParkingType?: string;
  compParkingType?: string;
}): boolean => {
  if (!isDefined(subjectHasFeature) || !isDefined(compHasFeature)) return false;
  if (subjectHasFeature !== compHasFeature) return true;
  if (!subjectHasFeature || !compHasFeature) return false;

  if (
    isDefined(subjectParkingType) &&
    isDefined(compParkingType) &&
    subjectParkingType === compParkingType &&
    isDefined(subjectSpaces) &&
    isDefined(compSpaces)
  ) {
    return Math.abs(compSpaces - subjectSpaces) > GARAGE_CARPORT_SPACE_MATERIALITY_THRESHOLD;
  }

  return false;
};

// Basement. Material when presence differs, or when both have a basement and total area or finished
// area differ (any non-zero mismatch counts; no percentage threshold).
export const isBasementAdjustmentMaterial = ({
  subjectHasFeature,
  compHasFeature,
  subjectTotalArea,
  compTotalArea,
  subjectFinishedArea,
  compFinishedArea,
}: {
  subjectHasFeature?: boolean;
  compHasFeature?: boolean;
  subjectTotalArea?: AloftArea;
  compTotalArea?: AloftArea;
  subjectFinishedArea?: AloftArea;
  compFinishedArea?: AloftArea;
}): boolean => {
  if (!isDefined(subjectHasFeature) || !isDefined(compHasFeature)) return false;
  if (subjectHasFeature !== compHasFeature) return true;
  if (!subjectHasFeature || !compHasFeature) return false;

  if (
    isDefined(subjectTotalArea) &&
    isDefined(compTotalArea) &&
    !subjectTotalArea.equals(AloftArea.zero) &&
    !compTotalArea.equals(AloftArea.zero) &&
    !subjectTotalArea.equals(compTotalArea)
  ) {
    return true;
  }

  return (
    isDefined(subjectFinishedArea) &&
    isDefined(compFinishedArea) &&
    !subjectFinishedArea.equals(AloftArea.zero) &&
    !compFinishedArea.equals(AloftArea.zero) &&
    !subjectFinishedArea.equals(compFinishedArea)
  );
};

// Basement bath count. Same thresholds as above-grade baths via isBathAdjustmentMaterial.
export const isBasementBathAdjustmentMaterial = (
  subjectFullBaths: number | undefined,
  subjectHalfBaths: number | undefined,
  compFullBaths: number | undefined,
  compHalfBaths: number | undefined,
): boolean =>
  isBathAdjustmentMaterial(subjectFullBaths, subjectHalfBaths, compFullBaths, compHalfBaths);

// Binary present/absent features (e.g. pool, fireplace). Material when both values are known and differ.
export const isPresentOrNotAdjustmentMaterial = (
  subjectHasFeature: boolean | undefined,
  compHasFeature: boolean | undefined,
): boolean =>
  isDefined(subjectHasFeature) && isDefined(compHasFeature) && subjectHasFeature !== compHasFeature;
